// NEWS (order app) — announcements UI; keep wired from Navigator / Header / OrderApp.
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Pagination, Paper, Typography } from "@mui/material";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { formatNewsDate } from "../../../functions/src/api/dates";
import { getExistingPdfDownloadUrl } from "../../api/imageUtils";
import { auth } from "../../api/firebase";
import { getLangValue, langContext } from "../../api/Lang";
import LoadingSpinner from "../../api/LoadingSpinner";
import { NewsAttachment } from "../../types";
import { useNewsStrings } from "./news-strings";
import { useNewsUnread, type NewsListEntry } from "./useNewsUnread";
import "./news.css";

const PAGE_SIZE = 10;

const newsAttachmentUrlCache = new Map<string, string>();

function useNewsAttachmentUrl(storagePath: string): string | null {
    const [url, setUrl] = useState<string | null>(() => newsAttachmentUrlCache.get(storagePath) ?? null);

    useEffect(() => {
        const cached = newsAttachmentUrlCache.get(storagePath);
        if (cached) {
            setUrl(cached);
            return;
        }
        let cancelled = false;
        getExistingPdfDownloadUrl(storagePath).then((downloadUrl) => {
            if (cancelled || !downloadUrl) return;
            newsAttachmentUrlCache.set(storagePath, downloadUrl);
            setUrl(downloadUrl);
        });
        return () => {
            cancelled = true;
        };
    }, [storagePath]);

    return url;
}

function NewsAttachmentLink({ attachment }: { attachment: NewsAttachment }) {
    const strings = useNewsStrings();
    const url = useNewsAttachmentUrl(attachment.storagePath);

    const onDownload = async (event: React.MouseEvent) => {
        event.preventDefault();
        if (!url) return;
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = attachment.fileName;
            link.click();
            URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.warn("Could not download news attachment:", err);
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    const content = (
        <>
            <img src="/img-assets/file-generic.svg" alt="" />
            {attachment.fileName}
        </>
    );

    if (!url) {
        return <span className="news-item-attachment news-item-attachment--pending">{content}</span>;
    }

    return (
        <a
            className="news-item-attachment news-item-attachment--ready"
            href={url}
            onClick={(event) => void onDownload(event)}
            aria-label={strings.attachmentAria}
        >
            {content}
        </a>
    );
}

function NewsAccordionItem({
    item,
    expanded,
    unread,
    onToggle,
    formatDate,
}: {
    item: NewsListEntry;
    expanded: boolean;
    unread: boolean;
    onToggle: () => void;
    formatDate: (ts: number) => string;
}) {
    const [lang] = useContext(langContext);
    const title = getLangValue(item.title, lang);
    const body = getLangValue(item.body, lang);

    return (
        <div className={`news-item${expanded ? " expanded" : ""}${unread ? " unread" : ""}`}>
            <button type="button" className="news-item-header" onClick={onToggle} aria-expanded={expanded}>
                <div className="news-item-title-row">
                    {unread && <span className="news-unread-dot" aria-hidden="true" />}
                    <span className="news-item-title">{title}</span>
                </div>
                <div className="news-item-meta">
                    <span className="news-item-date">{formatDate(item.publishedAt)}</span>
                    {expanded
                        ? <ExpandLessIcon className="news-item-chevron" />
                        : <ExpandMoreIcon className="news-item-chevron" />
                    }
                </div>
            </button>
            {expanded && (
                <div className="news-item-body">
                    <p className="news-item-text">{body}</p>
                    {item.attachments && Object.keys(item.attachments).length > 0 && (
                        <div className="news-item-attachments">
                            {Object.entries(item.attachments).map(([attachmentId, attachment]) => (
                                <NewsAttachmentLink key={attachmentId} attachment={attachment} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function News() {
    const [lang] = useContext(langContext);
    const strings = useNewsStrings();
    const uid = auth.currentUser?.uid;
    const { activeNews, loading, isItemUnread, markItemRead } = useNewsUnread(uid);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    const totalPages = Math.max(1, Math.ceil(activeNews.length / PAGE_SIZE));
    const pageItems = useMemo(
        () => activeNews.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
        [activeNews, page]
    );
    const rangeFrom = activeNews.length === 0 ? 0 : page * PAGE_SIZE + 1;
    const rangeTo = Math.min((page + 1) * PAGE_SIZE, activeNews.length);

    useEffect(() => {
        setPage(0);
    }, [activeNews]);

    useEffect(() => {
        if (page >= totalPages) {
            setPage(Math.max(0, totalPages - 1));
        }
    }, [page, totalPages]);

    useEffect(() => {
        listRef.current?.scrollTo(0, 0);
    }, [page]);

    useEffect(() => {
        if (activeNews.length === 0) {
            setExpandedId(null);
            return;
        }
        setExpandedId((prev) => (prev && pageItems.some((item) => item.id === prev) ? prev : null));
    }, [activeNews, pageItems]);

    useEffect(() => {
        if (expandedId) markItemRead(expandedId);
    }, [expandedId, markItemRead]);

    const handleToggle = (itemId: string) => {
        setExpandedId((prev) => (prev === itemId ? null : itemId));
    };

    const formatDate = (ts: number) => formatNewsDate(ts, lang);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (activeNews.length === 0) {
        return (
            <div className="base_margin_setup" style={{ maxWidth: '660px' }}>
                <div className="news-page">
                    <div className="news-page-header">
                        <div className="order-heading news-page-title">{strings.navLabel}</div>
                    </div>
                    <Paper elevation={0} className="news-box news-box--empty">
                        <div className="news-empty">
                            <p className="news-empty-title">{strings.emptyTitle}</p>
                            <p className="news-empty-body">{strings.emptyBody}</p>
                        </div>
                    </Paper>
                </div>
            </div>
        );
    }

    return (
        <div className="base_margin_setup" style={{ maxWidth: '660px' }}>
            <div className="news-page">
                <div className="news-page-header">
                    <div className="order-heading news-page-title">{strings.navLabel}</div>
                </div>
                <Paper elevation={0} className="news-box">
                    <Box className="news-box-header">
                        <Typography variant="body2" sx={{ color: "var(--gray_txt)" }}>
                            {strings.messageCount(activeNews.length)}
                        </Typography>
                    </Box>
                    <div ref={listRef} className="news-list">
                        {pageItems.map((item) => (
                            <NewsAccordionItem
                                key={item.id}
                                item={item}
                                expanded={expandedId === item.id}
                                unread={isItemUnread(item)}
                                onToggle={() => handleToggle(item.id)}
                                formatDate={formatDate}
                            />
                        ))}
                    </div>
                    <Box
                        className={`wallet-history-pagination news-box-pagination${activeNews.length > PAGE_SIZE ? "" : " news-box-pagination--hidden"}`}
                    >
                        {activeNews.length > PAGE_SIZE && (
                            <>
                                <Typography variant="caption" component="span" className="wallet-history-more">
                                    {strings.pageSummary(rangeFrom, rangeTo, activeNews.length)}
                                </Typography>
                                <Pagination
                                    count={totalPages}
                                    page={page + 1}
                                    onChange={(_, value) => setPage(value - 1)}
                                    size="small"
                                    color="primary"
                                    sx={{
                                        "& .MuiPaginationItem-root.Mui-selected": {
                                            backgroundColor: "var(--lb_green)",
                                        },
                                    }}
                                />
                            </>
                        )}
                    </Box>
                </Paper>
            </div>
        </div>
    );
}
